import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Banknote, CreditCard, RefreshCw, ShieldCheck, X } from 'lucide-react';
import { StripePaymentMethodForm, stripePromise } from '../StripePaymentMethodForm';
import { Note } from './financeUi';
import {
  billingApi, describeAttachOutcome, describeAuditOutcome, describePaymentMethod,
  describeRemovalFailure, describeRemoveOutcome,
  isBankVerificationRefusal, isRemovalFailure, isRemovalRefusal,
  type PaymentMethodOutcome, type PaymentMethodsResult, type StoredPaymentMethod,
} from '../../services/billingApi';
import { RubyLoader } from '../RubyLoader';

import { RubyGemMark } from '../RubyGemMark';
/**
 * FINANCE → PAYMENT METHOD
 *
 *   > *"show current payment method … super admin can update the card payment
 *   > or ACH bank info, or remove or replace. logs will be written."*
 *
 * The same capability platform-app already gives a tenant on
 * `/subscription-setup`, on the operator's side of the glass (spec §14/C3).
 *
 * ===========================================================================
 * CARD AND BANK DETAILS NEVER TOUCH OUR SERVERS
 * ===========================================================================
 * A card is collected by `StripePaymentMethodForm` — the SAME Stripe Elements
 * component platform-app uses, not a second one — and a bank account by
 * `stripe.collectBankAccountForSetup`, which opens Stripe's own dialog against
 * a SetupIntent. Both hand back an opaque `pm_…` id, and that id is the only
 * thing this component ever sends to our API.
 *
 * There is no field on this screen for a card number, a CVC, an account number
 * or a routing number, and there must never be one. The account-holder name and
 * email the ACH flow asks for go STRAIGHT to Stripe from the browser — they are
 * arguments to `collectBankAccountForSetup`, never to `billingApi`.
 *
 * ===========================================================================
 * THIS IS CUSTOMER-LEVEL, NOT LOCATION-LEVEL
 * ===========================================================================
 * Stripe attaches a payment method to a CUSTOMER, and this platform keeps one
 * Customer per tenant with one subscription per location. So changing the method
 * here changes how EVERY location is charged. The screen says that in as many
 * words rather than letting its position next to a per-location table imply
 * otherwise.
 *
 * ===========================================================================
 * A CHANGE TO MONEY IS REPORTED, NOT ASSUMED
 * ===========================================================================
 * Making a method the default is TWO writes at Stripe — the customer default,
 * then every subscription that pins its own method — and they can come apart.
 * So nothing here says what it ASKED for; every sentence comes from what the
 * server said happened, through `describeAttachOutcome` /
 * `describeRemoveOutcome` / `describeRemovalFailure` in `billingApi.ts`. Those
 * are pure and tested, because a sentence about how a tenant gets charged is
 * not something a type checker can keep honest.
 *
 * The server's own wording — `defaultError`, `replaceError`, `promotionWarning`,
 * the removal warning — is rendered VERBATIM. This component never composes a
 * second version of a rule the server applies.
 */

interface Props {
  open: boolean;
  tenantId: string;
  tenantLabel?: string;
  data: PaymentMethodsResult | null;
  loading: boolean;
  error: string | null;
  /** Reload the methods. Reloads THIS panel's data only — never the locations table. */
  onReload: () => Promise<void> | void;
  onClose: () => void;
  notify: (message: string) => void;
}

/** Which sub-flow the operator is in. `null` = just looking at the list. */
type Flow =
  | null
  | { kind: 'add-card' }
  | { kind: 'replace-card'; method: StoredPaymentMethod }
  | { kind: 'add-bank' }
  | { kind: 'replace-bank'; method: StoredPaymentMethod }
  | { kind: 'remove'; method: StoredPaymentMethod };

/**
 * A bank account Stripe will not let us charge yet.
 *
 * The refusal used to be a dead end: the operator was told the account cannot
 * be the default and given nothing to do about it. `collectBankAccountForSetup`
 * has ALREADY attached it to the customer by this point, so the one thing that
 * can still be done is record it and leave the charged method alone — which is
 * what `showPutOnFile` offers. It is false when the refusal came from the list
 * (the method is visibly on file already, so there is nothing to put anywhere).
 */
interface BankHold {
  paymentMethodId: string;
  /** The SERVER'S sentence. Rendered verbatim. */
  message: string;
  setupIntentStatus: string;
  showPutOnFile: boolean;
  /** For a refused REPLACE: the method that was therefore kept. */
  keptMethodLabel?: string;
}

const MethodIcon: React.FC<{ isBank: boolean }> = ({ isBank }) => (
  <div className="w-10 h-10 rounded-xl bg-stone-900 flex items-center justify-center shrink-0">
    {isBank ? <Banknote size={17} className="text-white" /> : <CreditCard size={17} className="text-white" />}
  </div>
);

const PaymentMethodPanel: React.FC<Props> = ({
  open, tenantId, tenantLabel, data, loading, error, onReload, onClose, notify,
}) => {
  const [flow, setFlow] = useState<Flow>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  // The ACH sub-form. Both fields are Stripe's own requirement for
  // `collectBankAccountForSetup` and are passed DIRECTLY to it.
  const [holderName, setHolderName] = useState('');
  const [holderEmail, setHolderEmail] = useState('');

  // The removal confirmation. `warning` is the SERVER'S sentence, rendered
  // verbatim — this component never composes its own version of the rule.
  const [removalWarning, setRemovalWarning] = useState<string | null>(null);
  const [removalReason, setRemovalReason] = useState('');

  /**
   * What the LAST call actually did, in the server's own terms. Held in state
   * rather than only toasted: a half-applied default change is not something to
   * flash for four seconds and lose.
   */
  const [outcome, setOutcome] = useState<PaymentMethodOutcome | null>(null);
  /** What that call's own audit row did. Null when there is nothing to say. */
  const [auditNote, setAuditNote] = useState<string | null>(null);
  const [bankHold, setBankHold] = useState<BankHold | null>(null);

  const resetFlow = useCallback(() => {
    setFlow(null);
    setFailure(null);
    setRemovalWarning(null);
    setRemovalReason('');
    setHolderName('');
    setHolderEmail('');
    setBankHold(null);
  }, []);

  // Closing the panel clears the last outcome too — reopening it must not show
  // a report about a call the operator has already dealt with.
  useEffect(() => {
    if (!open) { resetFlow(); setOutcome(null); setAuditNote(null); }
  }, [open, resetFlow]);

  if (!open) return null;

  const methods = data?.paymentMethods ?? [];
  // `[] || fallback` returns `[]` — an empty array is truthy. Length, always.
  const hasMethods = methods.length > 0;
  const liveSubscriptions = data?.liveSubscriptions ?? [];
  const locationCount = liveSubscriptions.filter((s) => !!s.locationId).length;
  const unreadable = !!data && data.readable === false;

  const run = async (key: string, fn: () => Promise<void>) => {
    setBusy(key);
    setFailure(null);
    setOutcome(null);
    setAuditNote(null);
    try {
      await fn();
    } catch (e: any) {
      setFailure(e?.message || 'That did not work.');
    } finally {
      setBusy(null);
    }
  };

  /** Start a sub-flow with nothing left over from the last one on screen. */
  const beginFlow = (next: Flow) => {
    setFailure(null);
    setOutcome(null);
    setAuditNote(null);
    setBankHold(null);
    setRemovalWarning(null);
    setRemovalReason('');
    setFlow(next);
  };

  /** How a method already on file reads, for a sentence that names it. */
  const labelOf = (id: string): string | undefined => {
    const m = methods.find((x) => x.id === id);
    return m ? describePaymentMethod(m).label : undefined;
  };

  /**
   * The ONE place a server answer becomes something the operator is told.
   * Both halves are reported: what happened to the money, and what happened to
   * the record of it.
   */
  const report = (
    o: PaymentMethodOutcome,
    audited: { auditRecorded?: boolean; auditDegraded?: boolean; auditError?: string },
  ) => {
    setOutcome(o);
    setAuditNote(describeAuditOutcome(audited).message);
    notify(o.message);
  };

  /** Send an id we just got from Stripe to our API, and refresh. */
  const submitPaymentMethodId = async (paymentMethodId: string, replaces?: string) => {
    // Stripe hands back the same id for an instrument already on file, and the
    // server refuses a replace of a method by itself. There is nothing to swap.
    const replacesId = replaces && replaces !== paymentMethodId ? replaces : undefined;
    try {
      const res = await billingApi.attachPaymentMethod(tenantId, {
        paymentMethodId,
        makeDefault: true,
        ...(replacesId ? { replacesPaymentMethodId: replacesId } : {}),
      });
      report(describeAttachOutcome(res), res);
    } catch (e: any) {
      if (isBankVerificationRefusal(e)) {
        // Nothing about how this tenant is charged was changed — but Stripe's
        // own dialog has already attached the account to the customer, so this
        // is not a failure to recover from, it is a state to be told about.
        setBankHold({
          paymentMethodId: e.bankVerification.paymentMethodId,
          message: e.bankVerification.message,
          setupIntentStatus: e.bankVerification.setupIntentStatus,
          showPutOnFile: true,
          keptMethodLabel: replacesId ? labelOf(replacesId) : undefined,
        });
        return;
      }
      throw e;
    }
    resetFlow();
    await onReload();
  };

  /**
   * Record a bank account that cannot be charged yet, WITHOUT pointing anything
   * at it.
   *
   * `replacesPaymentMethodId` is deliberately not sent: the server forces
   * `makeDefault` on for a replace, which is the very thing just refused. The
   * old method therefore stays, and the note above says so.
   */
  const keepOnFile = (hold: BankHold) => run('bank-hold', async () => {
    const res = await billingApi.attachPaymentMethod(tenantId, {
      paymentMethodId: hold.paymentMethodId,
      makeDefault: false,
    });
    report(describeAttachOutcome(res), res);
    resetFlow();
    await onReload();
  });

  /**
   * ACH. Stripe's dialog collects the account; we never see it.
   *
   * `collectBankAccountForSetup` attaches the resulting method to the customer
   * itself, so the follow-up `attachPaymentMethod` call is a no-op attach that
   * exists to make it the default AND to write the audit row — which is the
   * whole point of doing this here rather than in the Stripe dashboard.
   *
   * It also drives the REPLACE of a bank account, where the same call carries
   * `replacesPaymentMethodId` and the server does the swap in the safe order.
   * That swap can be refused outright: an account still awaiting microdeposit
   * verification cannot become the default, and the old one is then kept.
   */
  const startBankFlow = () => run('bank', async () => {
    const replaces = flow?.kind === 'replace-bank' ? flow.method.id : undefined;
    if (!holderName.trim() || !holderEmail.trim()) {
      throw new Error('Stripe needs the account holder\'s name and email before it will open the bank dialog.');
    }
    const stripe = await stripePromise;
    if (!stripe) throw new Error('Stripe did not load. Refresh the page and try again.');

    const { clientSecret } = await billingApi.createPaymentSetupIntent(tenantId, ['us_bank_account']);
    if (!clientSecret) throw new Error('Stripe did not return a setup secret.');

    const collected = await stripe.collectBankAccountForSetup({
      clientSecret,
      params: {
        payment_method_type: 'us_bank_account',
        // Straight to Stripe. Never to our API.
        payment_method_data: { billing_details: { name: holderName.trim(), email: holderEmail.trim() } },
      },
      expand: ['payment_method'],
    });
    if (collected.error) throw new Error(collected.error.message || 'The bank account could not be collected.');

    let intent = collected.setupIntent;
    if (intent?.status === 'requires_confirmation') {
      const confirmed = await stripe.confirmUsBankAccountSetup(clientSecret);
      if (confirmed.error) throw new Error(confirmed.error.message || 'The mandate could not be confirmed.');
      intent = confirmed.setupIntent;
    }
    const pm: any = intent?.payment_method;
    const paymentMethodId = typeof pm === 'string' ? pm : pm?.id;
    if (!paymentMethodId) throw new Error('Stripe did not return a payment method for that bank account.');

    await submitPaymentMethodId(paymentMethodId, replaces);
  });

  /**
   * "Charge this."
   *
   * A 200 is NOT proof the method is now charged: the customer default and the
   * subscriptions that pin their own method are separate writes, and the server
   * reports how far it got. Announcing "Default payment method changed" from the
   * request alone was the same defect the backend had and fixed.
   */
  const makeDefault = (m: StoredPaymentMethod) => run(m.id, async () => {
    try {
      const res = await billingApi.attachPaymentMethod(tenantId, { paymentMethodId: m.id, makeDefault: true });
      report(describeAttachOutcome(res), res);
    } catch (e: any) {
      if (isBankVerificationRefusal(e)) {
        // Nothing changed, and there is nothing to put on file — this account is
        // already in the list the operator clicked it in. All that can be done
        // is wait for Stripe, so say so and offer no button that does nothing.
        setBankHold({
          paymentMethodId: e.bankVerification.paymentMethodId,
          message: e.bankVerification.message,
          setupIntentStatus: e.bankVerification.setupIntentStatus,
          showPutOnFile: false,
        });
        return;
      }
      throw e;
    }
    await onReload();
  });

  const confirmRemove = (m: StoredPaymentMethod, opts: { acknowledge?: boolean } = {}) =>
    run(`remove-${m.id}`, async () => {
      try {
        const res = await billingApi.removePaymentMethod(tenantId, m.id, {
          ...(opts.acknowledge ? { acknowledgeNoPaymentMethod: true, reason: removalReason.trim() } : {}),
        });
        report(describeRemoveOutcome(res), res);
        resetFlow();
        await onReload();
      } catch (e: any) {
        if (isRemovalRefusal(e)) {
          // The server declined and told us exactly why. Show ITS sentence and
          // keep the dialog open for the acknowledgement — never rewrite it.
          setRemovalWarning(e.refusal.warning);
          setFailure(e.refusal.requiresReason && !removalReason.trim()
            ? 'A reason is required — it is stored on the audit trail.'
            : null);
          return;
        }
        if (isRemovalFailure(e)) {
          // A DIFFERENT refusal: the replacement could not be promoted, so
          // nothing was detached. There is no acknowledgement that gets past
          // this one — a reason box here would be the wrong answer entirely.
          //
          // The customer default may still have moved, because that write comes
          // before the subscription repoints. When it did, the list on screen is
          // now stale about which method is charged, so it is re-read.
          const out = describeRemovalFailure(e);
          setOutcome(out);
          setAuditNote(describeAuditOutcome(e.removalFailure).message);
          notify(out.message);
          resetFlow();
          if (out.reloadRequired) await onReload();
          return;
        }
        throw e;
      }
    });

  // -------------------------------------------------------------------------

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-start justify-center p-3 sm:p-6 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-stone-100 text-stone-800 rounded-2xl border border-stone-200 shadow-xl max-w-2xl w-full my-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Dark header, per the platform's page-header standard. */}
        <div className="bg-stone-900 text-white rounded-t-2xl px-5 py-4 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-lg font-bold truncate">
              Payment method{tenantLabel ? ` — ${tenantLabel}` : ''}
            </h2>
            <p className="text-xs text-stone-300 mt-0.5">
              How this tenant pays us. Stored by Stripe — never on our servers.
            </p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              onClick={() => onReload()}
              disabled={loading}
              title="Reload from Stripe"
              className="text-stone-300 hover:text-white p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
            >
              {loading ? <RubyGemMark size={16} drawing color="currentColor" /> : <RefreshCw size={16} />}
            </button>
            <button
              onClick={onClose}
              aria-label="Close"
              className="text-stone-300 hover:text-white p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="p-5 space-y-4 text-stone-800">
          <Note>
            <strong>This applies to every location.</strong> Stripe attaches a payment method to a{' '}
            <em>Customer</em>, and this tenant has one Customer with one subscription per location — so the
            method chosen here is what collects{' '}
            {liveSubscriptions.length
              ? `all ${liveSubscriptions.length} live subscription${liveSubscriptions.length === 1 ? '' : 's'}`
              : 'any subscription they have'}
            {locationCount ? ` across ${locationCount} location${locationCount === 1 ? '' : 's'}` : ''}. It is
            not a per-location setting.
          </Note>

          {error && <Note tone="danger">{error}</Note>}
          {failure && <Note tone="danger">{failure}</Note>}

          {/*
            What the last call DID. `detail` is the server's own wording — the
            sentence it composed about a partial default change or a kept
            instrument — and is printed as given.
          */}
          {outcome && (
            <Note tone={outcome.tone}>
              <strong>{outcome.message}</strong>
              {outcome.detail.map((d, i) => (
                <span key={i} className="block mt-1 opacity-90">{d}</span>
              ))}
            </Note>
          )}

          {/*
            The audit row for that same call. Only ever rendered when there is
            something wrong with it — a full row says nothing, and a server that
            did not report says nothing either, because silence is honest and an
            invented reassurance is not.
          */}
          {auditNote && (
            <Note tone="warn"><strong>Audit trail:</strong> {auditNote}</Note>
          )}

          {/* A bank account Stripe cannot charge yet, and the way forward. */}
          {bankHold && (
            <Note tone="warn">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <strong>This bank account cannot be charged yet.</strong>
                  {/* The server's sentence, verbatim. */}
                  <span className="block mt-1">{bankHold.message}</span>
                  {bankHold.keptMethodLabel && (
                    <span className="block mt-1">
                      Nothing was replaced — {bankHold.keptMethodLabel} is still on file.
                    </span>
                  )}
                </div>
                <button
                  onClick={() => setBankHold(null)}
                  aria-label="Dismiss"
                  className="text-amber-700 hover:text-amber-900 p-1 -m-1 shrink-0"
                >
                  <X size={15} />
                </button>
              </div>
              {bankHold.showPutOnFile && (
                <>
                  <span className="block mt-2">
                    Stripe has already attached it to this customer. Recording it here leaves the charged
                    method exactly as it is and puts the addition on the audit trail.{' '}
                    {bankHold.setupIntentStatus === 'canceled'
                      // Do not tell an operator to wait for something that will
                      // never arrive: a cancelled SetupIntent does not resume.
                      ? 'Stripe reports this account\u2019s setup as cancelled, which does not complete on its own — collect the account again before it can be charged.'
                      : 'Make it the charged method once Stripe reports the setup as succeeded.'}
                  </span>
                  <button
                    onClick={() => keepOnFile(bankHold)}
                    disabled={!!busy}
                    className="mt-2 px-4 py-2.5 rounded-xl bg-stone-900 text-white text-xs font-semibold hover:bg-black disabled:opacity-40 min-h-[44px]"
                  >
                    {busy === 'bank-hold' ? 'Recording…' : 'Keep it on file, not charged'}
                  </button>
                </>
              )}
            </Note>
          )}

          {/* -- what is on file ------------------------------------------ */}
          {loading && !data ? (
            <RubyLoader variant="section" className="py-10" />
          ) : unreadable ? (
            <Note tone="warn">
              <strong>The payment methods on file could not be read.</strong> This is not the same as there
              being none — nothing is shown because nothing is known.{' '}
              {data?.error ? <span className="block mt-1 opacity-80">{data.error}</span> : null}
            </Note>
          ) : data && !data.hasCustomer ? (
            <Note tone="warn">
              <strong>No Stripe customer yet.</strong> {data.note || 'One is created when this tenant completes onboarding.'}{' '}
              A payment method cannot be stored before then.
            </Note>
          ) : !hasMethods ? (
            <div className="rounded-xl border border-stone-200 bg-white px-4 py-8 text-center text-stone-800">
              <CreditCard size={30} className="mx-auto mb-3 text-stone-300" />
              <p className="text-sm font-semibold text-stone-700">Nothing on file</p>
              <p className="text-xs text-stone-500 mt-1">
                {liveSubscriptions.length
                  ? 'This tenant has live subscriptions and no way to pay them.'
                  : 'Add a card or a bank account when they are ready to be billed.'}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {methods.map((m) => {
                const d = describePaymentMethod(m);
                const working = busy === m.id || busy === `remove-${m.id}`;
                return (
                  <div
                    key={m.id}
                    className={`flex items-center gap-3 rounded-xl border px-3 py-3 bg-white text-stone-800 ${
                      m.isDefault ? 'border-emerald-300 ring-1 ring-emerald-100' : 'border-stone-200'
                    }`}
                  >
                    <MethodIcon isBank={d.isBank} />
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm font-semibold truncate ${d.unreadable ? 'text-amber-700' : 'text-stone-900'}`}>
                        {d.label}
                        {d.isBank && (
                          <span className="ml-2 text-[10px] uppercase tracking-wide text-sky-700 bg-sky-100 px-1.5 py-0.5 rounded">
                            ACH
                          </span>
                        )}
                      </p>
                      <p className={`text-xs ${d.expired ? 'text-red-600 font-semibold' : 'text-stone-500'}`}>{d.sub}</p>
                    </div>
                    {m.isDefault ? (
                      <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-100 px-2 py-1 rounded-full shrink-0">
                        Charged
                      </span>
                    ) : (
                      <button
                        disabled={!!busy}
                        onClick={() => makeDefault(m)}
                        className="text-xs font-semibold text-stone-600 hover:text-stone-900 px-2 min-h-[44px] shrink-0 disabled:opacity-40"
                      >
                        {working ? 'Working…' : 'Charge this'}
                      </button>
                    )}
                    {/*
                      A bank gets Replace too. The request was "update the card
                      payment or ACH bank info, or remove or replace", and
                      add-then-remove is a worse answer: it is the operator, not
                      the server, deciding the order, and there is nothing to
                      stop them detaching the old account first.
                    */}
                    <button
                      disabled={!!busy}
                      onClick={() => beginFlow(
                        d.isBank ? { kind: 'replace-bank', method: m } : { kind: 'replace-card', method: m },
                      )}
                      className="text-xs font-semibold text-stone-600 hover:text-stone-900 px-2 min-h-[44px] shrink-0 disabled:opacity-40"
                    >
                      Replace
                    </button>
                    <button
                      disabled={!!busy}
                      onClick={() => beginFlow({ kind: 'remove', method: m })}
                      className="text-xs font-semibold text-red-600 hover:text-red-800 px-2 min-h-[44px] shrink-0 disabled:opacity-40"
                    >
                      Remove
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* -- add ------------------------------------------------------- */}
          {data?.hasCustomer && !unreadable && !flow && (
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => beginFlow({ kind: 'add-card' })}
                className="px-4 py-2.5 rounded-xl bg-stone-900 text-white text-sm font-semibold hover:bg-black min-h-[44px] inline-flex items-center gap-2"
              >
                <CreditCard size={15} /> Add card
              </button>
              <button
                onClick={() => beginFlow({ kind: 'add-bank' })}
                className="px-4 py-2.5 rounded-xl border border-stone-300 bg-white text-stone-800 text-sm font-semibold hover:bg-stone-50 min-h-[44px] inline-flex items-center gap-2"
              >
                <Banknote size={15} /> Add bank account (ACH)
              </button>
            </div>
          )}

          {/* -- card form ------------------------------------------------- */}
          {(flow?.kind === 'add-card' || flow?.kind === 'replace-card') && (
            <div className="rounded-xl border border-stone-200 bg-white p-4 text-stone-800">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <h3 className="text-sm font-bold text-stone-900">
                    {flow.kind === 'replace-card' ? 'Replace this card' : 'Add a card'}
                  </h3>
                  {flow.kind === 'replace-card' && (
                    <p className="text-xs text-stone-500 mt-0.5">
                      The new card is attached and made the charged method <strong>before</strong> the old one
                      is removed, so this tenant is never left without a way to pay. If either step does not
                      fully apply, the old card is kept and this panel says so.
                    </p>
                  )}
                </div>
                <button onClick={resetFlow} className="text-stone-400 hover:text-stone-700 p-1 -m-1" aria-label="Cancel">
                  <X size={16} />
                </button>
              </div>
              {/*
                The SAME Elements component platform-app uses. `onClose` is
                deliberately wired to `resetFlow`, NOT to `onClose` — the form
                calls it itself two seconds after STRIPE accepts the card, and
                handing it the panel's close would tear the whole screen away
                before the list the operator came to check could be re-read.

                Stripe accepting the card is not us storing it: `onSuccess` fires
                first and `submitPaymentMethodId` runs after, so the outcome the
                operator acts on is the one reported below, not the form's own
                interstitial.
              */}
              <StripePaymentMethodForm
                tenantId={tenantId}
                onSuccess={(paymentMethodId: string) => {
                  const replaces = flow.kind === 'replace-card' ? flow.method.id : undefined;
                  run('card', () => submitPaymentMethodId(paymentMethodId, replaces));
                }}
                onClose={resetFlow}
              />
            </div>
          )}

          {/* -- ACH form (add AND replace) -------------------------------- */}
          {(flow?.kind === 'add-bank' || flow?.kind === 'replace-bank') && (
            <div className="rounded-xl border border-stone-200 bg-white p-4 text-stone-800">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <h3 className="text-sm font-bold text-stone-900">
                    {flow.kind === 'replace-bank'
                      ? `Replace ${describePaymentMethod(flow.method).label}`
                      : 'Add a bank account (ACH)'}
                  </h3>
                  <p className="text-xs text-stone-500 mt-0.5">
                    Stripe opens its own dialog to collect the account. The account and routing numbers go
                    directly to Stripe and are never sent to us.
                  </p>
                  {flow.kind === 'replace-bank' && (
                    <p className="text-xs text-stone-500 mt-1.5">
                      The new account is made the charged method <strong>before</strong> the old one is
                      removed, so this tenant is never left without a way to pay. A brand-new account often
                      has to clear Stripe's microdeposit check first — until it does it cannot be charged, so
                      the swap is refused, nothing is removed and the old account stays exactly as it is.
                    </p>
                  )}
                </div>
                <button onClick={resetFlow} className="text-stone-400 hover:text-stone-700 p-1 -m-1" aria-label="Cancel">
                  <X size={16} />
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-stone-500">
                    Account holder name
                  </span>
                  <input
                    value={holderName}
                    onChange={(e) => setHolderName(e.target.value)}
                    placeholder="Acme Coffee LLC"
                    className="mt-1 w-full rounded-lg border border-stone-300 bg-white px-3 py-2.5 text-sm text-stone-900 min-h-[44px]"
                  />
                </label>
                <label className="block">
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-stone-500">
                    Account holder email
                  </span>
                  <input
                    value={holderEmail}
                    onChange={(e) => setHolderEmail(e.target.value)}
                    placeholder="owner@acme.com"
                    className="mt-1 w-full rounded-lg border border-stone-300 bg-white px-3 py-2.5 text-sm text-stone-900 min-h-[44px]"
                  />
                </label>
              </div>
              <p className="text-[11px] text-stone-500 mt-2 flex items-start gap-1.5">
                <ShieldCheck size={13} className="mt-0.5 shrink-0 text-emerald-600" />
                Stripe requires these two fields for the ACH mandate. They are passed straight to Stripe from
                this browser — they never reach our API.
              </p>
              <button
                onClick={startBankFlow}
                disabled={busy === 'bank'}
                className="mt-3 w-full px-4 py-3 rounded-xl bg-stone-900 text-white text-sm font-bold hover:bg-black disabled:opacity-40 min-h-[44px]"
              >
                {busy === 'bank' ? 'Opening Stripe…' : 'Continue in Stripe'}
              </button>
            </div>
          )}

          {/* -- remove ---------------------------------------------------- */}
          {flow?.kind === 'remove' && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-red-900">
              <div className="flex items-start justify-between gap-3">
                <h3 className="text-sm font-bold text-red-900 flex items-center gap-2">
                  <AlertTriangle size={15} /> Remove {describePaymentMethod(flow.method).label}?
                </h3>
                <button onClick={resetFlow} className="text-red-400 hover:text-red-700 p-1 -m-1" aria-label="Cancel">
                  <X size={16} />
                </button>
              </div>

              {/* The server's own sentence, verbatim. */}
              {removalWarning && (
                <p className="mt-3 text-sm leading-relaxed font-semibold">{removalWarning}</p>
              )}

              {removalWarning && (
                <label className="block mt-3">
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-red-800">
                    Why (stored on the audit trail)
                  </span>
                  <input
                    value={removalReason}
                    onChange={(e) => setRemovalReason(e.target.value)}
                    placeholder="e.g. card reported stolen by the merchant"
                    className="mt-1 w-full rounded-lg border border-red-300 bg-white px-3 py-2.5 text-sm text-stone-900 min-h-[44px]"
                  />
                </label>
              )}

              <div className="flex gap-3 mt-4">
                <button
                  onClick={resetFlow}
                  className="flex-1 px-4 py-3 rounded-xl bg-white border border-red-200 text-red-800 font-semibold min-h-[44px]"
                >
                  Keep it
                </button>
                <button
                  onClick={() => confirmRemove(flow.method, { acknowledge: !!removalWarning })}
                  disabled={!!busy || (!!removalWarning && !removalReason.trim())}
                  className="flex-1 px-4 py-3 rounded-xl bg-red-600 text-white font-bold hover:bg-red-700 disabled:opacity-40 min-h-[44px]"
                >
                  {busy ? 'Working…' : removalWarning ? 'Remove anyway' : 'Remove'}
                </button>
              </div>
            </div>
          )}

          <p className="text-[11px] text-stone-500 leading-relaxed">
            Every add, replace, default change and removal is recorded on this tenant's audit trail with who
            did it and when. That record is written <em>after</em> Stripe has already changed, so it can fail
            on its own — when it does, or when only a reduced row could be written, this panel says so above
            rather than leaving it unsaid. Card and bank details are held by Stripe and never reach our
            servers: we hold a payment-method reference and what Stripe reports about it — type, brand, last
            four, expiry and funding — and nothing else.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentMethodPanel;
